import { useMemo } from "react";
import { fileBadgeColors } from "./FileBadge";
import { File as DrsFile } from "graphql/_Types";
import { useProcessFileFailedTaskError } from "hooks/useProcessFileFailedTaskError";

export interface UseFileBadgeTooltipContentProps {
  fileId: DrsFile["id"];
  fileName: DrsFile["name"];
  isUploading: boolean;
  isUploadFailed: boolean;
  isProcessing: boolean;
  isUnreadable: boolean;
  isArchived: boolean;
  isUnarchiving: boolean;
  isDataDeleted: boolean;
  isMisplaced: boolean;
  uploadProgress?: number;
}

export const useFileBadgeTooltipContent = ({
  fileId,
  fileName,
  isUploading,
  isUploadFailed,
  isProcessing,
  isUnreadable,
  isArchived,
  isUnarchiving,
  isDataDeleted,
  isMisplaced,
  uploadProgress,
}: UseFileBadgeTooltipContentProps) => {
  const {
    loading: isFailedTaskLoading,
    errorDescription: failedTaskErrorDescription,
  } = useProcessFileFailedTaskError(fileId);

  const tooltipContent = useMemo(() => {
    const tooltipFragments: { content: string; color?: string }[] = [
      { content: fileName },
    ];

    const addTooltipFragment = (message: string, color?: string) => {
      tooltipFragments.push({ content: message, color });
    };

    if (isUploading) {
      addTooltipFragment(`Uploading ${(uploadProgress ?? 0).toString()}%...`);
    }

    if (isUploadFailed) {
      addTooltipFragment("Upload failed", fileBadgeColors.uploadFailed);
    }

    if (isProcessing) {
      addTooltipFragment(
        "Extracting metadata and generating preview...",
        fileBadgeColors.processing,
      );
    }

    if (isUnreadable) {
      if (failedTaskErrorDescription !== undefined) {
        addTooltipFragment(
          failedTaskErrorDescription,
          fileBadgeColors.unreadable,
        );
      } else if (isFailedTaskLoading) {
        addTooltipFragment(
          "Loading error details...",
          fileBadgeColors.unreadable,
        );
      }
    }

    if (isArchived) {
      addTooltipFragment("Archived", fileBadgeColors.archived);
    }

    if (isUnarchiving) {
      addTooltipFragment("Requested unarchive", fileBadgeColors.unarchiving);
    }

    if (isDataDeleted) {
      addTooltipFragment("Data deleted", fileBadgeColors.dataDeleted);
    }

    if (isMisplaced) {
      addTooltipFragment(
        "File does not match assigned column's data type",
        fileBadgeColors.misplaced,
      );
    }

    return tooltipFragments;
  }, [
    failedTaskErrorDescription,
    fileName,
    isArchived,
    isDataDeleted,
    isFailedTaskLoading,
    isMisplaced,
    isProcessing,
    isUnarchiving,
    isUnreadable,
    isUploadFailed,
    isUploading,
    uploadProgress,
  ]);

  return tooltipContent;
};
